export type SendTemplatePreviewButton = {
  id: string
  type: string
  text: string
}

export type SendTemplateCarouselCardPreview = {
  id: string
  mediaType: 'IMAGE' | 'VIDEO' | ''
  mediaUrl: string
  body: string
  buttons: SendTemplatePreviewButton[]
}

export type SendTemplateLimitedTimeOfferPreview = {
  heading: string
  expirationDate: number | null
  offerCode: string
}

export type SendTemplatePreviewModel = {
  header: {
    show: boolean
    type: string
    mediaUrl: string
    text: string
  }
  body: string
  footer: string
  buttons: SendTemplatePreviewButton[]
  limitedTimeOffer: SendTemplateLimitedTimeOfferPreview | null
  carouselCards: SendTemplateCarouselCardPreview[]
}

/**
 * Meta/API example shape used by Bulk Actions WhatsappActionModal preview
 * (ghl-crm-frontend PR #10877 reference).
 */
type TemplateComponentExample = {
  timestamp?: number | number[]
  header_text?: string[] | string[][]
  body_text?: string[] | string[][]
  footer_text?: string[] | string[][]
}

type TemplateComponent = {
  type?: string
  format?: string
  text?: string
  customVariables?: unknown[]
  example?: TemplateComponentExample
  /** Meta LTO stores offer title under nested limited_time_offer.text */
  limited_time_offer?: { text?: string; has_expiration?: boolean }
  buttons?: Array<{
    type?: string
    text?: string
    offerCode?: string
    url?: string
    example?: string | string[]
    payload?: string
    customVariables?: unknown[]
    customVariable?: string
  }>
  cards?: Array<{ components?: TemplateComponent[] }>
}

export type WhatsAppTemplateLike = {
  templateType?: string
  components?: TemplateComponent[]
}

const emptyModel = (): SendTemplatePreviewModel => ({
  header: { show: false, type: '', mediaUrl: '', text: '' },
  body: '',
  footer: '',
  buttons: [],
  limitedTimeOffer: null,
  carouselCards: [],
})

export const isLtoOrCarouselTemplate = (template: WhatsAppTemplateLike | null | undefined): boolean => {
  if (!template) return false
  const templateType = (template.templateType || '').toUpperCase()
  if (templateType === 'LIMITED_TIME_OFFER' || templateType === 'CAROUSEL') return true
  return Boolean(
    template.components?.some(component => {
      const type = (component.type || '').toUpperCase()
      return type === 'CAROUSEL' || type === 'LIMITED_TIME_OFFER'
    })
  )
}

const SAFE_MEDIA_URL = /^(https?:\/\/|blob:)/i

const replaceVariables = (text: string, customVariables?: unknown[]): string => {
  if (!customVariables?.length) return text
  let result = text
  customVariables.forEach((value, index) => {
    if (typeof value === 'string') {
      result = result.replace(new RegExp(`\\{\\{${index + 1}}\\}`, 'g'), value)
    }
  })
  return result
}

/**
 * Returns example/sample values from component.example, handling both storage formats:
 *   flat array ["Alice", "3pm"] → keep the whole list
 *   nested array [["Alice", "3pm"]] → unwrap the first inner list
 */
const getExampleValues = (
  component: TemplateComponent | undefined,
  propName: 'header_text' | 'body_text' | 'footer_text'
): string[] | null => {
  const raw = component?.example?.[propName]
  if (!Array.isArray(raw) || raw.length === 0) return null
  const vals = Array.isArray(raw[0]) ? raw[0] : raw
  const strings = vals.filter((value): value is string => typeof value === 'string')
  return strings.length ? strings : null
}

/**
 * Prefer Meta/API example samples, then editor customVariables — mirrors
 * `getExampleValues(...) ?? component.customVariables ?? []` in WhatsappActionModal.
 */
const resolveSubstitutionValues = (
  component: TemplateComponent | undefined,
  propName: 'header_text' | 'body_text' | 'footer_text'
): unknown[] => getExampleValues(component, propName) ?? component?.customVariables ?? []

const resolveComponentText = (
  component: TemplateComponent | undefined,
  propName: 'header_text' | 'body_text' | 'footer_text'
): string => {
  if (!component?.text) return ''
  const values = resolveSubstitutionValues(component, propName)
  return values.length ? replaceVariables(component.text, values) : component.text
}

/** Allow only http(s)/blob media URLs for preview. */
export const sanitizeMediaUrl = (url: string | null | undefined): string => {
  if (!url || url.includes('{{')) return ''
  return SAFE_MEDIA_URL.test(url) ? url : ''
}

export const formatOfferExpirationDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  })

/**
 * Preview-safe media URL for header customVariables.
 * Prefers sampleLink so dynamic Media URL expressions (`.../{{1}}.jpg`) do not blank the preview.
 */
export const resolvePreviewMediaUrl = (customVariable: unknown, format?: string): string => {
  if (!customVariable || typeof customVariable !== 'object') return ''
  const media = customVariable as Record<string, unknown>
  if (typeof media.sampleLink === 'string' && media.sampleLink) {
    const sample = sanitizeMediaUrl(media.sampleLink)
    if (sample) return sample
  }
  const key = (format || String(media.type || '')).toLowerCase()
  const nested = key ? media[key] : undefined
  if (nested && typeof nested === 'object' && typeof (nested as { link?: string }).link === 'string') {
    const link = sanitizeMediaUrl((nested as { link: string }).link)
    if (link) return link
  }
  if (typeof media.url === 'string') {
    const url = sanitizeMediaUrl(media.url)
    if (url) return url
  }
  return typeof media.sampleLink === 'string' ? sanitizeMediaUrl(media.sampleLink) : ''
}

const parseTimestamp = (timestamp: unknown): number | null => {
  const value = Array.isArray(timestamp) ? timestamp[0] : timestamp
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const mapButtons = (buttons: TemplateComponent['buttons'] = []): SendTemplatePreviewButton[] =>
  buttons.map((button, index) => ({
    id: `btn-${index}-${button.type || 'unknown'}`,
    type: button.type || '',
    text: button.text || '',
  }))

const isLimitedTimeOfferComponent = (component: TemplateComponent | undefined): boolean => {
  const type = (component?.type || '').toUpperCase()
  return type === 'LIMITED_TIME_OFFER'
}

const resolveOfferHeading = (offerComponent: TemplateComponent | undefined): string => {
  const nested = offerComponent?.limited_time_offer?.text
  if (typeof nested === 'string' && nested.trim()) return nested.trim()
  return (offerComponent?.text || '').trim()
}

const resolveOfferCode = (buttons: TemplateComponent['buttons'] = []): string => {
  const copyCode = buttons.find(button => (button.type || '').toUpperCase() === 'COPY_CODE')
  if (!copyCode) return ''
  if (typeof copyCode.offerCode === 'string' && copyCode.offerCode) return copyCode.offerCode
  const example = copyCode.example
  if (typeof example === 'string' && example) return example
  if (Array.isArray(example) && typeof example[0] === 'string') return example[0]
  if (typeof copyCode.text === 'string' && copyCode.text.trim()) return copyCode.text.trim()
  return ''
}

const findComponentByType = (components: TemplateComponent[], type: string): TemplateComponent | undefined =>
  components.find(component => (component.type || '').toUpperCase() === type.toUpperCase())

const findCardComponent = (components: TemplateComponent[], type: string): TemplateComponent | undefined =>
  findComponentByType(components, type)

type CarouselCardLike = {
  components?: TemplateComponent[]
  body?: string | { text?: string; customVariables?: unknown[] }
  buttons?: TemplateComponent['buttons']
}

const resolveCardBodyText = (card: CarouselCardLike, bodyComponent?: TemplateComponent): string => {
  if (bodyComponent?.text) {
    return resolveComponentText(bodyComponent, 'body_text')
  }
  if (typeof card.body === 'string') {
    return card.body
  }
  if (card.body && typeof card.body === 'object' && typeof card.body.text === 'string') {
    return replaceVariables(card.body.text, card.body.customVariables)
  }
  return ''
}

const mapCarouselCard = (card: CarouselCardLike, index: number): SendTemplateCarouselCardPreview => {
  const components = card.components || []
  const header = findCardComponent(components, 'HEADER')
  const body = findCardComponent(components, 'BODY')
  const buttonsComponent = findCardComponent(components, 'BUTTONS')
  const buttons = buttonsComponent?.buttons?.length ? buttonsComponent.buttons : card.buttons || []
  const headerFormat = (header?.format || '').toUpperCase()
  const mediaType: 'IMAGE' | 'VIDEO' | '' = headerFormat === 'IMAGE' || headerFormat === 'VIDEO' ? headerFormat : ''

  return {
    id: `carousel-card-${index}`,
    mediaType,
    mediaUrl: resolvePreviewMediaUrl(header?.customVariables?.[0], header?.format || mediaType),
    body: resolveCardBodyText(card, body),
    buttons: mapButtons(buttons),
  }
}

export const buildSendTemplatePreviewModel = (template: WhatsAppTemplateLike | null | undefined): SendTemplatePreviewModel => {
  if (!template?.components?.length) return emptyModel()

  const components = template.components
  const header = findComponentByType(components, 'HEADER')
  const body = findComponentByType(components, 'BODY')
  const footer = findComponentByType(components, 'FOOTER')
  const buttonsComponent = findComponentByType(components, 'BUTTONS')
  const offerComponent = components.find(isLimitedTimeOfferComponent)
  const carouselComponent = findComponentByType(components, 'CAROUSEL')

  const heading = resolveOfferHeading(offerComponent)
  const expirationDate = parseTimestamp(offerComponent?.example?.timestamp)
  const offerCode = resolveOfferCode(buttonsComponent?.buttons)
  // Show LTO chrome when an LTO component/templateType is present, or any offer fields resolve.
  const isLtoTemplateType = (template.templateType || '').toUpperCase() === 'LIMITED_TIME_OFFER'
  const limitedTimeOffer: SendTemplateLimitedTimeOfferPreview | null =
    offerComponent || isLtoTemplateType || heading || expirationDate !== null || offerCode
      ? {
          heading,
          expirationDate,
          offerCode,
        }
      : null

  const headerFormat = (header?.format || (header?.text ? 'TEXT' : '')).toUpperCase()
  const isMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)
  const headerText = headerFormat === 'TEXT' ? resolveComponentText(header, 'header_text') : ''
  const headerMediaUrl = isMediaHeader
    ? resolvePreviewMediaUrl(header?.customVariables?.[0], headerFormat)
    : ''
  // Only mark header present when the UI can render text, document chrome, or a media URL.
  const headerShow =
    (headerFormat === 'TEXT' && Boolean(headerText)) ||
    headerFormat === 'DOCUMENT' ||
    (['IMAGE', 'VIDEO'].includes(headerFormat) && Boolean(headerMediaUrl))

  return {
    header: {
      show: headerShow,
      type: headerFormat,
      mediaUrl: headerMediaUrl,
      text: headerText,
    },
    body: resolveComponentText(body, 'body_text'),
    footer: resolveComponentText(footer, 'footer_text'),
    buttons: mapButtons(buttonsComponent?.buttons),
    limitedTimeOffer,
    carouselCards: (carouselComponent?.cards || []).map(mapCarouselCard),
  }
}

/** Compact snapshot persisted on outbound WA template messages for Conversations thread rendering. */
export type WhatsAppTemplateDisplaySnapshot = {
  limitedTimeOffer: {
    heading: string
    expirationMs: number | null
    offerCode: string
  } | null
  carouselCards: Array<{
    id: string
    mediaType: 'IMAGE' | 'VIDEO' | ''
    mediaUrl: string
    body: string
    buttons: SendTemplatePreviewButton[]
  }>
}

export type WhatsAppLimitedTimeOfferPlaceholder = {
  type: 'limited_time_offer'
  /** expiration_time_ms omitted when template has_expiration=false */
  limited_time_offer: { expiration_time_ms?: number; text?: string }
}

export type WhatsAppCarouselCardPlaceholder = {
  card_index: number
  components: Array<Record<string, unknown>>
}

export const toWhatsAppTemplateDisplaySnapshot = (
  model: SendTemplatePreviewModel
): WhatsAppTemplateDisplaySnapshot | null => {
  const limitedTimeOffer = model.limitedTimeOffer
    ? {
        heading: model.limitedTimeOffer.heading,
        expirationMs: model.limitedTimeOffer.expirationDate,
        offerCode: model.limitedTimeOffer.offerCode,
      }
    : null
  const carouselCards = model.carouselCards.map(card => ({
    id: card.id,
    mediaType: card.mediaType,
    mediaUrl: card.mediaUrl,
    body: card.body,
    buttons: card.buttons,
  }))

  if (!limitedTimeOffer && !carouselCards.length) return null
  return { limitedTimeOffer, carouselCards }
}

export const getLimitedTimeOfferTimestamp = (
  template: WhatsAppTemplateLike | null | undefined
): number | null => {
  const offerComponent = template?.components?.find(isLimitedTimeOfferComponent)
  return parseTimestamp(offerComponent?.example?.timestamp)
}

/**
 * Meta/CRM send placeholder for LTO. No-expiration templates (has_expiration=false,
 * no example.timestamp) still get a heading-only placeholder so Conversations can
 * render the offer title; Meta omits the send component when expiration is absent.
 */
export const buildLimitedTimeOfferPlaceholders = (
  template: WhatsAppTemplateLike | null | undefined
): WhatsAppLimitedTimeOfferPlaceholder[] => {
  if (!template) return []
  const hasLto =
    (template.templateType || '').toUpperCase() === 'LIMITED_TIME_OFFER' ||
    Boolean(template.components?.some(isLimitedTimeOfferComponent))
  if (!hasLto) return []
  const timestamp = getLimitedTimeOfferTimestamp(template)
  const heading = resolveOfferHeading(template.components?.find(isLimitedTimeOfferComponent))
  if (timestamp === null && !heading) return []
  return [
    {
      type: 'limited_time_offer',
      limited_time_offer: {
        ...(timestamp !== null ? { expiration_time_ms: timestamp } : {}),
        ...(heading ? { text: heading } : {}),
      },
    },
  ]
}

const resolveCarouselCardMediaLink = (header: TemplateComponent | undefined): string => {
  const mediaType = (header?.format || '').toLowerCase()
  const customVariable = header?.customVariables?.[0]
  if (!customVariable || !mediaType || typeof customVariable !== 'object') return ''
  return resolvePreviewMediaUrl(customVariable, mediaType)
}

/** Meta send placeholders for carousel cards (CRM SendTemplateModal shape, sync). */
export const buildCarouselPlaceholders = (
  template: WhatsAppTemplateLike | null | undefined
): WhatsAppCarouselCardPlaceholder[] => {
  const component = findComponentByType(template?.components || [], 'CAROUSEL')
  if (!component?.cards?.length) return []

  return component.cards.map((card, cardIndex) => {
    const components = card.components || []
    const cardComponents: Array<Record<string, unknown>> = []
    const header = findCardComponent(components, 'HEADER')
    const headerFormat = (header?.format || '').toUpperCase()
    if (header && ['IMAGE', 'VIDEO'].includes(headerFormat)) {
      const mediaType = headerFormat.toLowerCase()
      const mediaLink = resolveCarouselCardMediaLink(header)
      if (mediaLink) {
        cardComponents.push({
          type: 'header',
          parameters: [{ type: mediaType, [mediaType]: { link: mediaLink } }],
        })
      }
    }

    const body = findCardComponent(components, 'BODY')
    const bodyText = typeof body?.text === 'string' ? body.text : ''
    // Match preview precedence: prefer Meta/API example.body_text samples, then editor customVariables.
    const bodyValues = resolveSubstitutionValues(body, 'body_text').filter(
      (text): text is string => typeof text === 'string'
    )
    if (bodyValues.length) {
      cardComponents.push({
        type: 'body',
        ...(bodyText ? { text: bodyText } : {}),
        parameters: bodyValues.map(text => ({ type: 'text', text })),
      })
    } else if (bodyText) {
      cardComponents.push({
        type: 'body',
        text: bodyText,
      })
    }

    const buttons = findCardComponent(components, 'BUTTONS')?.buttons || []
    const paneButtons = buttons
      .filter(button => typeof button.text === 'string' && button.text)
      .map(button => ({ type: button.type, text: button.text }))
    if (paneButtons.length) {
      cardComponents.push({
        type: 'BUTTONS',
        buttons: paneButtons,
      })
    }
    buttons.forEach((button, index) => {
      const buttonType = (button.type || '').toUpperCase()
      if (buttonType === 'QUICK_REPLY') {
        const payload = button.payload ?? (typeof button.customVariables?.[0] === 'string' ? button.customVariables[0] : undefined) ?? button.text
        if (payload) {
          cardComponents.push({
            type: 'button',
            sub_type: 'quick_reply',
            index,
            parameters: [{ type: 'payload', payload }],
          })
        }
        return
      }
      if (buttonType === 'URL') {
        // Prefer singular customVariable (template/DB), then customVariables[0].
        // example[] is sample-only and must not become Meta {{1}} when a merge field exists.
        const mergeField =
          (typeof button.customVariable === 'string' && button.customVariable.trim()
            ? button.customVariable
            : undefined) ??
          (typeof button.customVariables?.[0] === 'string' ? button.customVariables[0] : undefined)
        const exampleValue = Array.isArray(button.example)
          ? button.example[0]
          : typeof button.example === 'string'
            ? button.example
            : undefined

        if (typeof mergeField === 'string' && mergeField) {
          cardComponents.push({
            type: 'button',
            sub_type: 'url',
            index,
            parameters: [{ type: 'text', customVariable: mergeField }],
          })
        } else if (typeof exampleValue === 'string' && exampleValue) {
          cardComponents.push({
            type: 'button',
            sub_type: 'url',
            index,
            parameters: [{ type: 'text', text: exampleValue }],
          })
        }
      }
    })

    return { card_index: cardIndex, components: cardComponents }
  })
}

/** Build LTO/carousel Meta placeholders with in-field pane copy for calendar send paths. */
export const enrichWhatsAppPlaceholdersForLtoCarousel = (
  placeholders: Record<string, unknown>,
  template: WhatsAppTemplateLike | null | undefined
): { placeholders: Record<string, unknown> } => {
  const next = { ...placeholders }
  const lto = buildLimitedTimeOfferPlaceholders(template)
  if (lto.length) next.limited_time_offer = lto

  const carousel = buildCarouselPlaceholders(template)
  if (carousel.length) next.carousel = carousel

  return { placeholders: next }
}

const LOOKS_LIKE_DYNAMIC_PLACEHOLDER = /\{\{[^}]+\}\}/
const INTERNAL_MEDIA_HEADER_KEYS = ['mediaSource', 'urlCustomVariable', 'urlSampleContent', 'sampleLink'] as const

const isDynamicPlaceholderValue = (value: unknown): boolean =>
  typeof value === 'string' && LOOKS_LIKE_DYNAMIC_PLACEHOLDER.test(value)

const getButtonExampleValue = (button: { example?: string | string[] } | undefined): string | undefined => {
  if (!button) return undefined
  if (Array.isArray(button.example) && typeof button.example[0] === 'string' && button.example[0].trim()) {
    return button.example[0]
  }
  if (typeof button.example === 'string' && button.example.trim()) {
    return button.example
  }
  return undefined
}

/**
 * Test-send only: bake Media URL headers to sampleLink / concrete https links and strip
 * authoring-only keys so lc-phone validateLinkInPlaceholder / Meta accept the payload.
 * Live notifications must keep urlCustomVariable for runtime contact resolution.
 */
export const bakeWhatsAppMediaHeaderForTestSend = (
  placeholder: Record<string, unknown>
): Record<string, unknown> => {
  if (!placeholder || typeof placeholder !== 'object') return placeholder

  const mediaType = String(placeholder.type || '').toLowerCase()
  if (!['image', 'video', 'document'].includes(mediaType)) {
    return placeholder
  }

  const bakedLink = resolvePreviewMediaUrl(placeholder, mediaType)
  const cleaned: Record<string, unknown> = { ...placeholder }
  for (const key of INTERNAL_MEDIA_HEADER_KEYS) {
    delete cleaned[key]
  }

  if (bakedLink) {
    const media = (cleaned[mediaType] as Record<string, unknown> | undefined) || {}
    cleaned.type = mediaType
    cleaned[mediaType] = { ...media, link: bakedLink }
  }

  return cleaned
}

const bakeCarouselPlaceholdersForTestSend = (
  carousel: WhatsAppCarouselCardPlaceholder[],
  template: WhatsAppTemplateLike | null | undefined
): WhatsAppCarouselCardPlaceholder[] => {
  const templateCards = findComponentByType(template?.components || [], 'CAROUSEL')?.cards || []

  return carousel.map((card, cardIndex) => {
    if (!card || typeof card !== 'object') return card

    const templateComponents = templateCards[cardIndex]?.components || []
    const templateBody = findCardComponent(templateComponents, 'BODY')
    const templateButtons = findCardComponent(templateComponents, 'BUTTONS')?.buttons || []
    const exampleBodyVals = getExampleValues(templateBody, 'body_text') || []

    const components = (card.components || []).map(component => {
      if (!component || typeof component !== 'object') return component
      const componentType = String(component.type || '').toLowerCase()

      if (componentType === 'header') {
        return {
          ...component,
          parameters: (component.parameters || []).map(parameter =>
            parameter && typeof parameter === 'object' && !Array.isArray(parameter)
              ? bakeWhatsAppMediaHeaderForTestSend(parameter as Record<string, unknown>)
              : parameter
          ),
        }
      }

      if (componentType === 'body') {
        return {
          ...component,
          parameters: (component.parameters || []).map((parameter, index) => {
            if (typeof parameter === 'string') {
              if (isDynamicPlaceholderValue(parameter) && exampleBodyVals[index]) {
                return exampleBodyVals[index]
              }
              return parameter
            }
            if (!parameter || typeof parameter !== 'object') return parameter
            const next = { ...(parameter as Record<string, unknown>) }
            const dynamic =
              isDynamicPlaceholderValue(next.text) || isDynamicPlaceholderValue(next.customVariable)
            if (dynamic && exampleBodyVals[index]) {
              return { type: 'text', text: exampleBodyVals[index] }
            }
            return next
          }),
        }
      }

      if (componentType === 'button') {
        const buttonIndex = typeof component.index === 'number' ? component.index : 0
        const exampleValue = getButtonExampleValue(templateButtons[buttonIndex])
        const subType = String(component.sub_type || '').toLowerCase()
        if (subType !== 'url') return component

        return {
          ...component,
          parameters: (component.parameters || []).map(parameter => {
            if (!parameter || typeof parameter !== 'object') return parameter
            const next = { ...(parameter as Record<string, unknown>) }
            const dynamic =
              isDynamicPlaceholderValue(next.customVariable) || isDynamicPlaceholderValue(next.text)
            if (dynamic && exampleValue) {
              return { type: 'text', text: exampleValue }
            }
            if (typeof next.customVariable === 'string' && !isDynamicPlaceholderValue(next.customVariable)) {
              next.type = next.type || 'text'
              next.text = next.customVariable
              delete next.customVariable
              delete next.originalURL
              return next
            }
            if (next.customVariable) {
              delete next.customVariable
              delete next.originalURL
            }
            return next
          }),
        }
      }

      return component
    })

    return { ...card, components }
  })
}

/**
 * Test-notification only: replace dynamic merge fields / Media URL tokens with Meta example
 * samples so send-test works without contact runtime resolution.
 * Do not call from live calendar notification processors.
 */
export const bakeWhatsAppPlaceholdersForTestSend = (
  placeholders: Record<string, unknown>,
  template?: WhatsAppTemplateLike | null
): Record<string, unknown> => {
  if (!placeholders || typeof placeholders !== 'object') return placeholders

  const next: Record<string, unknown> = { ...placeholders }

  if (Array.isArray(next.header)) {
    next.header = next.header.map(item =>
      item && typeof item === 'object' && !Array.isArray(item)
        ? bakeWhatsAppMediaHeaderForTestSend(item as Record<string, unknown>)
        : item
    )
  }

  if (Array.isArray(next.buttons)) {
    next.buttons = next.buttons.map(button => {
      if (!button || typeof button !== 'object') return button
      const btn = { ...(button as Record<string, unknown>) }
      if (String(btn.type || '').toUpperCase() !== 'URL') return btn

      const exampleValue = getButtonExampleValue(btn as { example?: string | string[] })
      const urlDynamic = isDynamicPlaceholderValue(btn.url)
      if (
        exampleValue &&
        (isDynamicPlaceholderValue(btn.customVariable) || (urlDynamic && !btn.customVariable))
      ) {
        btn.customVariable = exampleValue
      }
      return btn
    })
  }

  if (Array.isArray(next.carousel)) {
    next.carousel = bakeCarouselPlaceholdersForTestSend(
      next.carousel as WhatsAppCarouselCardPlaceholder[],
      template
    )
  }

  return next
}
